import { Suspense, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";
import { WEBSEITE_NEU_FUER_ALLE } from "@/lib/webseite/weiche";
import { useWebseiteNeu } from "./useWebseiteNeu";

/**
 * Was beim Laden der neuen Fassung dasteht. Vor dem Start die alte Fassung (für das Team in der Vorschau, damit nichts
 * leer ist). Seit dem Start nie die alte: Bei langsamer Verbindung stand sonst knapp zwei Sekunden die alte Seite mit
 * alten Preisen da (Sitzung SB, gemessen bei 1,6 Mbit/s auf `/preise`). Dann eine leere Fläche im Grund der Webseite,
 * beim Kopf und Fuß nichts (die Seiten halten die 80 px des Kopfs selbst frei).
 */
function SeiteLaedt() {
  return <div aria-busy="true" className="min-h-screen" style={{ background: "#FAF9F5" }} />;
}

/**
 * Eine Route mit zwei Fassungen (Weiche „Webseite neu“, `src/lib/webseite/weiche.ts`). Ist die Weiche aus, steht
 * hier genau das alte Element, ohne Hülle und ohne zusätzliches Markup. Die neue Fassung kommt per `lazy`; was
 * steht, solange sie lädt, sagt `SeiteLaedt` oben.
 */
export function WebseiteWeiche({ alt, neu }: { alt: ReactNode; neu: ReactNode }) {
  const aktiv = useWebseiteNeu();
  if (!aktiv) return <>{alt}</>;
  return <Suspense fallback={WEBSEITE_NEU_FUER_ALLE ? <SeiteLaedt /> : alt}>{neu}</Suspense>;
}

/**
 * Für den alten Kopf und Fuß, die rund 40 alte Seiten selbst einbinden (beide ohne Eigenschaften): Mit Weiche
 * zeichnet derselbe Import den neuen Kopf oder Fuß, ohne Weiche genau den alten.
 */
export function mitWebseiteWeiche(Alt: ComponentType, Neu: LazyExoticComponent<ComponentType>) {
  function MitWeiche() {
    const aktiv = useWebseiteNeu();
    if (!aktiv) return <Alt />;
    return (
      <Suspense fallback={WEBSEITE_NEU_FUER_ALLE ? null : <Alt />}>
        <Neu />
      </Suspense>
    );
  }
  MitWeiche.displayName = `MitWebseiteWeiche(${Alt.displayName ?? Alt.name ?? "Komponente"})`;
  return MitWeiche;
}
